import { useEffect, useRef, useCallback } from 'react';
import { HubConnectionBuilder, HubConnection, LogLevel, HttpTransportType } from '@microsoft/signalr';

const API_BASE_URL = import.meta.env.VITE_API_URL || '';

interface UseSignalROptions {
  hubPath: string;
  enabled?: boolean;
}

export function useSignalR({ hubPath, enabled = true }: UseSignalROptions) {
  const connectionRef = useRef<HubConnection | null>(null);

  useEffect(() => {
    if (!enabled) return;

    const token = localStorage.getItem('accessToken');
    if (!token) return;

    const connection = new HubConnectionBuilder()
      .withUrl(`${API_BASE_URL}/hub/${hubPath}`, {
        accessTokenFactory: () => localStorage.getItem('accessToken') || '',
        transport: HttpTransportType.WebSockets | HttpTransportType.LongPolling,
      })
      .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
      .configureLogging(LogLevel.Warning)
      .build();

    connectionRef.current = connection;

    connection.start().catch((err) => {
      console.error(`SignalR connection to ${hubPath} failed:`, err);
    });

    return () => {
      connection.stop();
      connectionRef.current = null;
    };
  }, [hubPath, enabled]);

  const on = useCallback(<T>(event: string, handler: (data: T) => void) => {
    connectionRef.current?.on(event, handler);
    return () => connectionRef.current?.off(event, handler);
  }, []);

  return { connection: connectionRef.current, on };
}
